import jsPDF from 'jspdf';
import { translations } from './translations';

const LOGO_URL = '/logo.jpg';
const SIGNATURE_URL = '/signatura-representant.png';

const loadImageAsBase64 = (url, format = 'jpeg') => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      try {
        resolve(canvas.toDataURL(`image/${format}`, format === 'jpeg' ? 0.9 : undefined));
      } catch (e) {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
};

export const generateAdoptionPDF = async (formData, language = 'ca') => {
  const t = (key) => translations[language]?.[key] || key;
  
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 12;
  const contentWidth = pageWidth - 2 * margin;
  let y = margin;

  // === HEADER: LOGO + INFO ASSOCIACIÓ (esquerra) + CAIXA DE DADES (dreta) ===
  const logoBase64 = await loadImageAsBase64(LOGO_URL);
  if (logoBase64) {
    try {
      doc.addImage(logoBase64, 'JPEG', margin, y, 32, 18);
    } catch (e) { /* ignore */ }
  }
  
  // Info associació sota el logo
  doc.setFontSize(7);
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'normal');
  const infoY = y + 20;
  doc.text(t('pdf_association_name'), margin, infoY);
  doc.text(t('pdf_association_nif'), margin, infoY + 3);
  doc.text(t('pdf_association_address'), margin, infoY + 6);
  doc.text(t('pdf_association_city'), margin, infoY + 9);
  doc.text(t('pdf_association_phone'), margin, infoY + 12);
  doc.text(t('pdf_association_email'), margin, infoY + 15);
  
  // Caixa de dades del gat (dreta)
  const boxX = margin + 85;
  const boxY = y;
  const boxW = pageWidth - margin - boxX;
  const boxH = 36;
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.3);
  doc.rect(boxX, boxY, boxW, boxH);
  
  // Camps dins la caixa
  const rowH = 5;
  doc.setFontSize(7.5);
  const drawBoxField = (label, value, col, row) => {
    const colW = boxW / 2;
    const x = boxX + (col * colW) + 2;
    const yPos = boxY + (row * rowH) + 4;
    doc.setFont('helvetica', 'normal');
    doc.text(label + ':', x, yPos);
    doc.setFont('helvetica', 'bold');
    const labelWidth = doc.getTextWidth(label + ': ');
    doc.text(value || '', x + labelWidth, yPos);
    // Línia divisora vertical
    if (col === 0) {
      doc.setLineWidth(0.2);
      doc.line(boxX + colW, boxY + (row * rowH), boxX + colW, boxY + ((row + 1) * rowH));
    }
    // Línia divisora horitzontal
    if (row < 6) {
      doc.line(boxX, boxY + ((row + 1) * rowH), boxX + boxW, boxY + ((row + 1) * rowH));
    }
  };
  
  drawBoxField(t('pdf_header_procedencia'), formData.procedencia, 0, 0);
  drawBoxField('', '', 1, 0);
  drawBoxField(t('pdf_header_nom'), formData.nombre_gato, 0, 1);
  drawBoxField(t('pdf_header_castracio'), formData.fecha_esterilizacion, 1, 1);
  drawBoxField(t('pdf_header_vacuna1'), formData.fecha_vacunacion, 0, 2);
  drawBoxField(t('pdf_header_vacuna2'), formData.fecha_vacunacion2, 1, 2);
  drawBoxField(t('pdf_header_test'), formData.test_felv, 0, 3);
  drawBoxField(t('pdf_header_chip'), formData.microchip, 1, 3);
  
  y = boxY + boxH + 5;

  // === TÍTOL ===
  const year = new Date().getFullYear();
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text(`${t('pdf_title')} ${year}`, pageWidth / 2, y, { align: 'center' });
  y += 6;

  // === COMPAREIXENT ===
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text(t('pdf_comparecing'), margin, y);
  y += 4;

  // === PART 1 (Adoptant) ===
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  const line1 = `${t('pdf_part1_intro')} ${formData.nombre || '___'} ${formData.apellidos || '___'}, ${t('pdf_part1_of_age')} ${formData.dni || '___'}, ${t('pdf_part1_domicile')} ${formData.direccion || '___'}.`;
  const wrapped1 = doc.splitTextToSize(line1, contentWidth);
  doc.text(wrapped1, margin, y);
  y += wrapped1.length * 3.5;
  
  const line2 = `${t('pdf_part1_cp')} ${formData.codigo_postal || '___'}, ${t('pdf_part1_pop')} ${formData.poblacion || '___'}, ${t('pdf_part1_phone')} ${formData.telefono || '___'}.`;
  const wrapped2 = doc.splitTextToSize(line2, contentWidth);
  doc.text(wrapped2, margin, y);
  y += wrapped2.length * 3.5;
  
  const line3 = `${t('pdf_part1_email')} ${formData.adoptante_email || '___'} ${t('pdf_part1_end')}`;
  const wrapped3 = doc.splitTextToSize(line3, contentWidth);
  doc.text(wrapped3, margin, y);
  y += wrapped3.length * 3.5 + 2;

  // === PART 2 (Associació) ===
  const wrapped4 = doc.splitTextToSize(t('pdf_part2'), contentWidth);
  doc.text(wrapped4, margin, y);
  y += wrapped4.length * 3.5 + 2;

  // === ACORD ===
  const wrapped5 = doc.splitTextToSize(t('pdf_agreement'), contentWidth);
  doc.text(wrapped5, margin, y);
  y += wrapped5.length * 3.5 + 3;

  // === PRIMER ===
  doc.setFont('helvetica', 'bold');
  doc.text(t('pdf_clause_1_title'), margin, y);
  doc.setFont('helvetica', 'normal');
  const clause1Text = ' ' + t('pdf_clause_1');
  const clause1Width = doc.getTextWidth(t('pdf_clause_1_title'));
  const wrapped6 = doc.splitTextToSize(clause1Text, contentWidth - clause1Width);
  doc.text(wrapped6[0], margin + clause1Width, y);
  for (let i = 1; i < wrapped6.length; i++) {
    y += 3.5;
    doc.text(wrapped6[i], margin, y);
  }
  y += 5;

  // === CARACTERÍSTIQUES DEL GAT ===
  const drawCheckbox = (x, yPos, checked) => {
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3);
    doc.rect(x, yPos - 2.5, 2.5, 2.5);
    if (checked) {
      doc.setFillColor(0, 0, 0);
      doc.rect(x + 0.4, yPos - 2.1, 1.7, 1.7, 'F');
    }
  };
  
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  
  // Fila 1: Espècie + Sexe
  const col1X = margin;
  const col2X = margin + 95;
  doc.setFont('helvetica', 'bold');
  doc.text(t('form_species') + ':', col1X, y);
  doc.setFont('helvetica', 'normal');
  drawCheckbox(col1X + 20, y, formData.especie === 'gat');
  doc.text(t('form_species_cat'), col1X + 24, y);
  drawCheckbox(col1X + 35, y, formData.especie === 'gos');
  doc.text(t('form_species_dog'), col1X + 39, y);
  
  doc.setFont('helvetica', 'bold');
  doc.text(t('form_sex') + ':', col2X, y);
  doc.setFont('helvetica', 'normal');
  drawCheckbox(col2X + 15, y, formData.sexe === 'mascle');
  doc.text(t('form_sex_male'), col2X + 19, y);
  drawCheckbox(col2X + 35, y, formData.sexe === 'femella');
  doc.text(t('form_sex_female'), col2X + 39, y);
  y += 4.2;
  
  // Fila 2: Nom + Edat + F. Naixement
  doc.setFont('helvetica', 'bold');
  doc.text(t('form_cat_name') + ':', col1X, y);
  doc.setFont('helvetica', 'normal');
  doc.text(formData.nombre_gato || '_____________', col1X + 20, y);
  
  doc.setFont('helvetica', 'bold');
  doc.text(t('form_cat_age') + ':', col2X, y);
  doc.setFont('helvetica', 'normal');
  doc.text(formData.edat_gat || '_______', col2X + 15, y);
  doc.setFont('helvetica', 'bold');
  doc.text(t('form_cat_birth') + ':', col2X + 40, y);
  doc.setFont('helvetica', 'normal');
  doc.text(formData.data_naixement || '_______', col2X + 65, y);
  y += 4.2;
  
  // Fila 3: Raça + Altres senyes
  doc.setFont('helvetica', 'bold');
  doc.text(t('form_cat_breed') + ':', col1X, y);
  doc.setFont('helvetica', 'normal');
  drawCheckbox(col1X + 15, y, formData.raca === 'europeu');
  doc.text(t('form_cat_breed_european'), col1X + 19, y);
  doc.text(formData.raca_altre || '', col1X + 50, y);
  
  doc.setFont('helvetica', 'bold');
  doc.text(t('form_cat_marks') + ':', col2X, y);
  doc.setFont('helvetica', 'normal');
  doc.text(formData.altres_senyes || '____________', col2X + 25, y);
  y += 4.2;
  
  // Fila 4: Color + Esterilitzat + Pèl
  doc.setFont('helvetica', 'bold');
  doc.text(t('form_cat_color') + ':', col1X, y);
  doc.setFont('helvetica', 'normal');
  doc.text(formData.color || '________', col1X + 15, y);
  
  doc.setFont('helvetica', 'bold');
  doc.text(t('form_sterilized') + ':', col1X + 45, y);
  doc.setFont('helvetica', 'normal');
  drawCheckbox(col1X + 68, y, formData.esterilitzat === 'si');
  doc.text(t('form_yes'), col1X + 72, y);
  drawCheckbox(col1X + 80, y, formData.esterilitzat === 'no');
  doc.text(t('form_no'), col1X + 84, y);
  
  doc.setFont('helvetica', 'bold');
  doc.text(t('form_cat_hair') + ':', col2X, y);
  doc.setFont('helvetica', 'normal');
  drawCheckbox(col2X + 12, y, formData.pel === 'curt');
  doc.text(t('form_cat_hair_short'), col2X + 16, y);
  drawCheckbox(col2X + 32, y, formData.pel === 'llarg');
  doc.text(t('form_cat_hair_long'), col2X + 36, y);
  y += 5;
  
  // Nº Microxip
  doc.setFont('helvetica', 'bold');
  doc.text(t('form_microchip') + ':', margin, y);
  doc.setFont('helvetica', 'normal');
  doc.text(formData.microchip || '________________', margin + 25, y);
  
  // Donatiu (dreta)
  if (formData.donatiu) {
    doc.setFont('helvetica', 'bold');
    doc.text(t('pdf_field_donation') + ':', margin + 95, y);
    doc.setFont('helvetica', 'normal');
    doc.text(formData.donatiu, margin + 115, y);
  }
  y += 5;

  // === SEGON ===
  doc.setFont('helvetica', 'bold');
  doc.text(t('pdf_clause_2_title'), margin, y);
  doc.setFont('helvetica', 'normal');
  const cw2 = doc.getTextWidth(t('pdf_clause_2_title'));
  const w2 = doc.splitTextToSize(' ' + t('pdf_clause_2'), contentWidth - cw2);
  doc.text(w2[0], margin + cw2, y);
  for (let i = 1; i < w2.length; i++) {
    y += 3.5;
    doc.text(w2[i], margin, y);
  }
  y += 4.5;

  // === TERCER ===
  doc.setFont('helvetica', 'bold');
  doc.text(t('pdf_clause_3_title'), margin, y);
  doc.setFont('helvetica', 'normal');
  const cw3 = doc.getTextWidth(t('pdf_clause_3_title'));
  const w3 = doc.splitTextToSize(' ' + t('pdf_clause_3'), contentWidth - cw3);
  doc.text(w3[0], margin + cw3, y);
  for (let i = 1; i < w3.length; i++) {
    y += 3.5;
    doc.text(w3[i], margin, y);
  }
  y += 4;

  // === CONFORMITAT ===
  const wConf = doc.splitTextToSize(t('pdf_conformity'), contentWidth);
  doc.text(wConf, margin, y);
  y += wConf.length * 3.5 + 3;

  // === PROTECCIÓ DE DADES ===
  doc.setFont('helvetica', 'bold');
  doc.text(t('pdf_data_protection'), margin, y);
  y += 4;
  doc.setFont('helvetica', 'normal');
  drawCheckbox(margin, y, true);
  doc.text(t('pdf_data_accept'), margin + 5, y);
  y += 6;

  // === SIGNATURES ===
  const sigCol1X = margin;
  const sigCol2X = margin + 95;

  // Signatura associació (esquerra)
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.text(`D./Dña. ${t('pdf_representative')}`, sigCol1X, y);
  doc.text(t('pdf_sign_association'), sigCol1X, y + 3);
  doc.text(t('pdf_sign_representative'), sigCol1X, y + 6);
  const today = new Date();
  const dateStr = `${today.getDate().toString().padStart(2, '0')}/${(today.getMonth() + 1).toString().padStart(2, '0')}/${today.getFullYear()}`;
  doc.text(`${t('pdf_sign_place')} ${dateStr}`, sigCol1X, y + 9);
  
  // Signatura escanejada del representant (sota les dades)
  const signBase64 = await loadImageAsBase64(SIGNATURE_URL, 'png');
  if (signBase64) {
    try {
      doc.addImage(signBase64, 'PNG', sigCol1X, y + 11, 32, 18);
    } catch (e) { /* ignore */ }
  }
  
  // Signatura adoptant (dreta)
  doc.text(`D./Dña. ${formData.nombre || ''} ${formData.apellidos || ''}`, sigCol2X, y);
  doc.text(t('pdf_sign_adopter'), sigCol2X, y + 3);
  doc.text(`DNI: ${formData.dni || ''}`, sigCol2X, y + 6);
  
  // Afegir signatura digital
  if (formData.firma && formData.firma.startsWith('data:image')) {
    try {
      doc.addImage(formData.firma, 'PNG', sigCol2X, y + 9, 38, 15);
    } catch (e) { /* ignore */ }
  }
  
  y += 30;

  // === ANNEX: DADES DEL VETERINARI (inline compacte) ===
  if (formData.veterinari_nom || formData.veterinari_collegiat) {
    doc.setDrawColor(150, 150, 150);
    doc.setLineWidth(0.3);
    doc.line(margin, y, pageWidth - margin, y);
    y += 3.5;
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text(t('pdf_annex_title'), margin, y);
    y += 3.5;
    
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');
    // Compactar en dues columnes
    const annexCol1 = margin;
    const annexCol2 = margin + 95;
    const addAnnexInline = (label, value, x, yPos) => {
      doc.setFont('helvetica', 'bold');
      doc.text(label + ':', x, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(value || '-', x + 35, yPos);
    };
    addAnnexInline(t('pdf_field_vet_name'), formData.veterinari_nom, annexCol1, y);
    addAnnexInline(t('pdf_field_vet_collegiate'), formData.veterinari_collegiat, annexCol2, y);
    y += 3.5;
    addAnnexInline(t('pdf_field_vet_card'), formData.veterinari_cartilla, annexCol1, y);
    addAnnexInline(t('pdf_field_vet_doc'), formData.veterinari_doc, annexCol2, y);
    y += 3.5;
    addAnnexInline(t('pdf_field_vet_aiac'), formData.veterinari_aiac_data, annexCol1, y);
  }

  // ===================================================
  // PÀGINA 2: CONDICIONS DE L'ADOPCIÓ
  // ===================================================
  doc.addPage();
  y = margin;
  
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text(t('pdf_conditions_title'), pageWidth / 2, y, { align: 'center' });
  y += 8;
  
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  
  const drawClause = (key) => {
    const text = t(key);
    const wrapped = doc.splitTextToSize(text, contentWidth);
    // Detectar el títol (ex: "PRIMER.-" o "PRIMERO.-")
    const match = text.match(/^([A-ZÀ-Úa-zà-ú]+\.-)\s/);
    if (match) {
      const title = match[1];
      const titleW = doc.getTextWidth(title);
      doc.setFont('helvetica', 'bold');
      doc.text(title, margin, y);
      doc.setFont('helvetica', 'normal');
      // La resta del text (primera línia)
      const restFirst = wrapped[0].substring(title.length);
      doc.text(restFirst, margin + titleW, y);
      for (let i = 1; i < wrapped.length; i++) {
        y += 3.5;
        doc.text(wrapped[i], margin, y);
      }
    } else {
      doc.text(wrapped, margin, y);
      y += (wrapped.length - 1) * 3.5;
    }
    y += 4;
  };
  
  drawClause('pdf_cond_1');
  drawClause('pdf_cond_2');
  drawClause('pdf_cond_3');
  drawClause('pdf_cond_4');
  drawClause('pdf_cond_5');
  drawClause('pdf_cond_6');
  drawClause('pdf_cond_7');
  drawClause('pdf_cond_8');
  drawClause('pdf_cond_9');
  drawClause('pdf_cond_10');
  
  // Donatiu
  if (formData.donatiu) {
    y += 2;
    doc.setFont('helvetica', 'bold');
    doc.text(`${t('pdf_field_donation').toUpperCase()}: ${formData.donatiu}`, margin, y);
    y += 5;
  }
  
  // Si no cap més contingut, nova pàgina per protecció de dades
  if (y > pageHeight - 90) {
    doc.addPage();
    y = margin;
  } else {
    y += 3;
  }
  
  // GDPR
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text(t('pdf_gdpr_title'), margin, y);
  y += 5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  const gdprWrapped = doc.splitTextToSize(t('pdf_gdpr_text'), contentWidth);
  doc.text(gdprWrapped, margin, y);
  y += gdprWrapped.length * 3.2 + 4;
  
  // Consentiment fotos
  doc.setFontSize(7.5);
  const photosWrapped = doc.splitTextToSize('- ' + t('pdf_consent_photos'), contentWidth - 20);
  doc.text(photosWrapped, margin, y);
  const photosH = photosWrapped.length * 3.2;
  drawCheckbox(pageWidth - margin - 18, y, formData.consent_photos === 'si');
  doc.text('SÍ', pageWidth - margin - 14, y);
  drawCheckbox(pageWidth - margin - 8, y, formData.consent_photos === 'no');
  doc.text('NO', pageWidth - margin - 4, y);
  y += photosH + 3;
  
  // Consentiment info
  const infoWrapped = doc.splitTextToSize('- ' + t('pdf_consent_info'), contentWidth - 20);
  doc.text(infoWrapped, margin, y);
  const infoH = infoWrapped.length * 3.2;
  drawCheckbox(pageWidth - margin - 18, y, formData.consent_info === 'si');
  doc.text('SÍ', pageWidth - margin - 14, y);
  drawCheckbox(pageWidth - margin - 8, y, formData.consent_info === 'no');
  doc.text('NO', pageWidth - margin - 4, y);
  y += infoH + 3;

  // === FOOTER ===
  const footerY = pageHeight - 8;
  doc.setFontSize(7);
  doc.setTextColor(120, 113, 108);
  doc.setFont('helvetica', 'italic');
  const timeStr = `${today.getHours().toString().padStart(2, '0')}:${today.getMinutes().toString().padStart(2, '0')}`;
  doc.text(`${t('pdf_generated')} ${dateStr} - ${timeStr}`, pageWidth / 2, footerY, { align: 'center' });

  return doc;
};

export const downloadAdoptionPDF = async (formData, language = 'ca') => {
  const doc = await generateAdoptionPDF(formData, language);
  const filename = `contracte_adopcio_${formData.nombre_gato || 'gat'}.pdf`;
  doc.save(filename);
};

// Retorna un Blob PDF (útil per pujar al NAS o compartir).
export const getAdoptionPdfBlob = async (formData, language = 'ca') => {
  const doc = await generateAdoptionPDF(formData, language);
  return doc.output('blob');
};

export const sendAdoptionByEmail = (formData, language = 'ca') => {
  const t = (key) => translations[language]?.[key] || key;
  
  const subject = encodeURIComponent(`${t('email_subject')} - ${formData.nombre_gato} - Gats del Carrer`);
  const body = encodeURIComponent(
    `${t('email_greeting')} ${formData.nombre},\n\n` +
    `${t('email_body')} ${formData.nombre_gato}.\n\n` +
    `${t('email_data_title')}\n` +
    `- ${t('form_date_adoption')}: ${formData.fecha_adopcion}\n` +
    `- ${t('form_microchip')}: ${formData.microchip}\n\n` +
    `${t('email_attach_note')}\n\n` +
    t('email_thanks')
  );
  
  window.location.href = `mailto:${formData.adoptante_email}?subject=${subject}&body=${body}`;
};
